import { useCallback, useEffect, useMemo, useState, type FunctionComponent } from "react";
import { Form } from "../../components/ui/form";
import { FormInput } from "../../components/form-components/form-input.components";
import { FormImageUpload } from "../../components/form-components/form-image-upload.components";
import type { UseFormReturn } from "react-hook-form";
import type { IzCampaignFormAdmin } from "../../z/admin/campaign-form.admin.z";
import { FormSelect } from "../../components/form-components/form-select.components";
import { FormDateInput } from "../../components/form-components/form-date-input.components";
import BudgetCard from "../../components/budget-card.components";
import MileStoneComponent from "../../components/milestone.components";
import { FormAssetUpload } from "../../components/form-components/form-asset.components";
import PrefixTextArea from "../../components/form-components/form-prefix-textarea.components";
import { FormTextArea } from "../../components/form-components/form-textarea.components";
import { Button } from "../../components/ui/button";
import {
  SocialCreatorType,
  SocialGender,
  SocialPlatforms,
} from "../../types/models/social.models.types";
import {
  type ICampaign,
  SocialPostType,
} from "../../types/models/campaign.models.types";
import LinksComponent from "../../components/links.components";
import { useAutocompleteCityCommonHttp } from "../../hooks/http/common/utils/google-maps/auto-complete-city.common.http";
import type { ILocationMinimal } from "../../types/models/location.models.types";
import { debounce, handleHttpError } from "../../utils/utils";
import { useToast } from "../../hooks/use-toast";
import { FormMultiSelectLocation } from "../../components/form-components/form-multiselect-location.components";
import { Separator } from "../../components/ui/separator";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FilterType } from "../../types/models/filter.models.types";
import {
  useGetAllFavoritesAdminHttp,
  type IFavoriteSocial,
} from "../../hooks/http/admin/favorites/get-all-favorites.admin.http";
import FixedPayouts from "../../components/fixed-payout.components";

// eslint-disable-next-line react-refresh/only-export-components
export const SocialPostTypeOptions: Record<
  SocialPostType.REEL | SocialPostType.YOUTUBE_SHORT,
  {
    value: SocialPostType;
    label: string;
  }
> = {
  [SocialPostType.REEL]: {
    value: SocialPostType.REEL,
    label: "Reel",
  },
  [SocialPostType.YOUTUBE_SHORT]: {
    value: SocialPostType.YOUTUBE_SHORT,
    label: "Short",
  },
};

export const SocialCreatorTypeOptions: Record<
  SocialCreatorType,
  {
    value: SocialCreatorType;
    label: string;
  }
> = {
  [SocialCreatorType.face]: {
    value: SocialCreatorType.face,
    label: "Face",
  },
  [SocialCreatorType.meme]: {
    value: SocialCreatorType.meme,
    label: "Meme",
  },
};

export const SocialGenderOptions: Record<
  SocialGender,
  {
    value: SocialGender;
    label: string;
  }
> = {
  [SocialGender.prefernotsay]: {
    value: SocialGender.prefernotsay,
    label: "No preference",
  },
  [SocialGender.male]: {
    value: SocialGender.male,
    label: "Male",
  },
  [SocialGender.female]: {
    value: SocialGender.female,
    label: "Female",
  },
  [SocialGender.other]: {
    value: SocialGender.other,
    label: "Other",
  },
};

export const SocialPlatformOptions: Record<
  SocialPlatforms,
  {
    value: SocialPlatforms;
    label: string;
  }
> = {
  [SocialPlatforms.instagram]: {
    value: SocialPlatforms.instagram,
    label: "Instagram",
  },
  [SocialPlatforms.youtube]: {
    value: SocialPlatforms.youtube,
    label: "Youtube",
  },
};
interface IProps {
  campaign?: ICampaign;
  form: UseFormReturn<IzCampaignFormAdmin>;
  onSubmit: (data: IzCampaignFormAdmin) => void;
}

const CampaignFormAdminPartial: FunctionComponent<IProps> = ({
  campaign,
  form,
  onSubmit,
}) => {
  const [maxBudget, setMaxBudget] = useState(100000);
  const [predictions, setPredictions] = useState<ILocationMinimal[]>([]);
  const [filterType, setFilterType] = useState<FilterType>(
    campaign?.favouriteSocial
      ? FilterType.FAVOTRITEGROUPS
      : FilterType.USEFILTERS,
  );
  const [favouriteGroups, setFavouriteGroups] = useState<IFavoriteSocial[]>([]);
  const { execute: getLocationHttp, fetching: fetchingLocation } =
    useAutocompleteCityCommonHttp();

  const { execute: getFavouriteGroupsHttp, fetching: fetchingFavouriteGroups } =
    useGetAllFavoritesAdminHttp();

  const { toast } = useToast();

  const selectedPlatform = form.watch("socialPlatform");

  const filteredSocialPostTypeOptions = useMemo(() => {
    if (selectedPlatform === SocialPlatforms.instagram) {
      // Only Reel for Instagram
      return [SocialPostTypeOptions[SocialPostType.REEL]];
    }

    if (selectedPlatform === SocialPlatforms.youtube) {
      // Only Short for YouTube (no Video option)
      return [SocialPostTypeOptions[SocialPostType.YOUTUBE_SHORT]];
    }

    // Default: show all options if no platform selected or other platforms added later
    return Object.values(SocialPostTypeOptions);
  }, [selectedPlatform]);

  useEffect(() => {
    if (!selectedPlatform) {
      // Clear post type when platform is deselected
      form.setValue("socialPostType", null);
      return;
    }
    const currentPostType = form.getValues("socialPostType");
    const isValidForPlatform = filteredSocialPostTypeOptions.some(
      (option) => option.value === currentPostType
    );
    if (!isValidForPlatform && filteredSocialPostTypeOptions.length > 0) {
      form.setValue("socialPostType", filteredSocialPostTypeOptions[0].value);
    }
  }, [selectedPlatform, filteredSocialPostTypeOptions, form]);

  async function getFavouriteGroups() {
    const response = await getFavouriteGroupsHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setFavouriteGroups(response.data.favouriteSocials);
  }

  const debouncedLocationSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }

      const response = await getLocationHttp({
        body: null,
        queryParams: {
          query: searchString,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }

      setPredictions(response.data.predictions);
    }, 500),
    [],
  );

  async function handleLocationChange(searchQuery: string) {
    debouncedLocationSearch(searchQuery);
  }

  function handleUploadChange(field: "coverImageFile", files: File[]) {
    const currentFiles = form.getValues(field) || [];
    form.setValue(field, [...currentFiles, ...files.map((file) => ({ file }))]);
  }

  function filterTypeChange() {
    if (filterType === FilterType.FAVOTRITEGROUPS) {
      return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        <FormSelect
          form={form}
          name="favouriteSocial"
          label="Favorite Group"
          placeholder="Select favorite group"
          onOpenChange={getFavouriteGroups}
          options={
            fetchingFavouriteGroups
              ? [
                  {
                    value: "1",
                    label: "Loading...",
                  },
                ]
              : favouriteGroups.map((group) => ({
                  value: group.id,
                  label: group.name,
                }))
          }
          disabled={fetchingFavouriteGroups}
        />
      );
    }
    if (filterType === FilterType.USEFILTERS) {
      return (
        <div className="gap-8 grid grid-cols-2">
          <div className="col-span-2">
            <FormSelect
              form={form}
              name="genderPreference"
              label="Gender preference"
              placeholder="Select preference"
              options={Object.values(SocialGenderOptions)}
              // disabled
            />
          </div>

          <FormInput
            form={form}
            name="minimumFollowers"
            label="Minimum followers"
            placeholder="Enter minimum followers"
            type="number"
            min={0}
          />
          <FormInput
            form={form}
            name="maximumFollowers"
            label="Maximum followers"
            placeholder="Enter maximum followers"
            type="number"
            min={0}
          />

          <div className="col-span-2">
            <FormMultiSelectLocation
              fetcher={handleLocationChange}
              form={form}
              name="locationPreference"
              label="Location"
              placeholder="Enter location"
              fetching={fetchingLocation}
              options={predictions.map((prediction) => ({
                placeId: prediction.placeId,
                address: prediction.address,
              }))}
            />
          </div>
        </div>
      );
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        onError={(formState) => console.log("Form error:", formState)}
        className="relative gap-8 grid grid-cols-2"
      >
        <FormInput
          form={form}
          name="name"
          label="Campaign Name"
          placeholder="Enter your campaign name"
          type="text"
          required
        />        <FormImageUpload
          form={form}
          name="coverImageFile"
          label="Campaign Image"
          maxSize={5 * 1024 * 1024}
          onValueChange={(files) => {
            handleUploadChange("coverImageFile", files);
          }}
        />
        <FormSelect
          form={form}
          name="socialPlatform"
          label="Select Platform"
          placeholder="Select Platform"
          required={true}
          options={Object.values(SocialPlatformOptions)}
          disabled={campaign !== undefined}
        />


        <FormSelect
          form={form}
          name="socialPostType"
          label="Post Type"
          placeholder="Select post type"
          required={true}
          options={filteredSocialPostTypeOptions}
          disabled={campaign !== undefined || !selectedPlatform}
        />

        <div className="flex flex-col gap-6 col-span-2 px-8 py-6 border border-border rounded-xl">
          <p className="font-semibold">How would you like to filter people?</p>
          <Separator />

          <div className="flex items-center gap-28 pb-5">
            <div className="flex items-center gap-2">
              <Input
                type="radio"
                id="use-favorite-group"
                checked={filterType === FilterType.FAVOTRITEGROUPS}
                className="w-5 h-5 accent-primarybuttonbg"
                value={filterType}
                onChange={() => {
                  setFilterType(FilterType.FAVOTRITEGROUPS);
                }}
              />
              <Label
                className="font-semibold cursor-pointer"
                htmlFor="use-favorite-group"
              >
                Use favorite group
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="radio"
                name="enableFilter"
                checked={filterType === FilterType.USEFILTERS}
                id="use-filters"
                className="w-5 h-5 accent-primarybuttonbg"
                value={filterType}
                onChange={() => setFilterType(FilterType.USEFILTERS)}
              />
              <Label
                className="font-semibold cursor-pointer"
                htmlFor="use-filters"
              >
                Use filters
              </Label>
            </div>
          </div>
          {filterTypeChange()}
        </div>

        <FormDateInput
          form={form}
          name="startDate"
          label="Start Date"
          placeholder="Pick a date"
          disabledDays={(date) =>
            date < new Date(new Date().setHours(0, 0, 0, 0))
          }
        />
        <FormDateInput
          form={form}
          name="endDate"
          label="End Date"
          placeholder="Pick a date"
          disabledDays={(date) => {
            const isWeekend = date.getDay() === 0 || date.getDay() === 6
            const startDate = form.getValues("startDate");
            if (startDate) {
              return isWeekend || new Date(date).getTime() <= new Date(startDate).getTime();
            }
            return isWeekend || date <= new Date();
          }}
        />

        <div className="col-span-2">
          <BudgetCard
            form={form}
            label="Choose budget"
            label2="Choose client budget"
            name="budget"
            name2="budgetInternal"
            maxBudget={maxBudget}
            setMaxBudget={setMaxBudget}
          />
        </div>

        <FormAssetUpload
          form={form}
          name="briefVideosFiles"
          trackingName="briefVideos"
          label="Campaign brief video"
          placeholder="upload video"
          uploadType="video"
          multiple={false}
          maxFiles={1}
          description="max size 100MB"
          maxSize={100 * 1024 * 1024}
          buttonLabel="Reupload"
        />

        <div className="col-span-2">
          <FixedPayouts form={form} />
        </div>

        <div className="col-span-2">
          <MileStoneComponent
            form={form}
            label1="Milestone Count"
            label2="Milestone payout"
          />
        </div>

        <div className="flex gap-8 col-span-2 mb-5">
          <FormAssetUpload
            form={form}
            name="assetFile"
            trackingName="assets"
            label="Campaign Assets"
            placeholder="upload assets"
            uploadType="all"
            multiple={true}
            maxFiles={5}
            description="max size 100MB"
            maxSize={100 * 1024 * 1024}
          />
          <FormAssetUpload
            form={form}
            name="sampleVideosFiles"
            trackingName="sampleVideos"
            label="Sample videos"
            placeholder="upload video"
            uploadType="video"
            multiple={true}
            maxFiles={5}
            description="max size 100MB"
            maxSize={100 * 1024 * 1024}
          />
        </div>

        <div className="col-span-2">
          <LinksComponent form={form} label1="Link title" label2="URL" />
        </div>

        <PrefixTextArea
          form={form}
          prefix="#"
          name="hashtags"
          label="Mandatory hashtags"
          placeholder="Enter necessary hashtags"
        />

        <PrefixTextArea
          form={form}
          prefix="@"
          name="mentions"
          label="Mandatory mentions"
          placeholder="Enter necessary mentions"
        />

        <div className="col-span-2">
          <FormTextArea
            form={form}
            name="aboutCampaign"
            label="About the campaign"
            placeholder="Enter about the campaign"
          />
        </div>

        <FormTextArea
          form={form}
          name="do"
          label="do's"
          placeholder="Enter do's"
        />
        <FormTextArea
          form={form}
          name="dont"
          label="don't"
          placeholder="Enter don't"
        />

        <FormTextArea
          form={form}
          name="aboutPayment"
          label="About the cash payment process"
          placeholder="Enter about the cash payment process"
        />
        <FormTextArea
          form={form}
          name="aboutBrand"
          label="About the brand"
          placeholder="Enter about the brand"
        />

        <Button
          type="submit"
          id="campaign-form-submit-admin"
          className="hidden"
        />
      </form>
    </Form>
  );
};

export default CampaignFormAdminPartial;
